/* eslint-disable react/no-unescaped-entities */
import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Loader2, Save, RotateCcw, Upload, Image as ImageIcon, ExternalLink, Palette } from "lucide-react";

/** Admin CMS — no-code edition of the public homepage.
 *  Backend SSOT : /api/cms/home (public) + PUT /api/admin/cms/home (admin).
 */
export default function AdminCMS() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/cms/home")
      .then(({ data }) => setData(data))
      .catch(() => toast.error("Impossible de charger le contenu actuel"))
      .finally(() => setLoading(false));
  }, []);

  const update = (k, v) => setData((d) => ({ ...d, [k]: v }));
  const updateStat = (i, key, v) => {
    const next = [...(data.about_stats || [])];
    next[i] = { ...next[i], [key]: v };
    update("about_stats", next);
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data: fresh } = await api.put("/admin/cms/home", data);
      setData(fresh);
      toast.success("Modifications enregistrées");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de la sauvegarde");
    } finally {
      setSaving(false);
    }
  };

  const resetAll = async () => {
    if (!window.confirm("Restaurer les valeurs par défaut ? Toutes vos personnalisations seront perdues.")) return;
    try {
      const { data: fresh } = await api.post("/admin/cms/home/reset");
      setData(fresh);
      toast.success("Valeurs par défaut restaurées");
    } catch {
      toast.error("Impossible de réinitialiser");
    }
  };

  if (loading || !data) {
    return (
      <div className="flex items-center justify-center min-h-[40vh]" data-testid="admin-cms-loading">
        <Loader2 className="w-7 h-7 animate-spin text-[#C68E59]" />
      </div>
    );
  }

  return (
    <div className="max-w-[1100px] mx-auto px-4 sm:px-6 py-8" data-testid="admin-cms-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-10">
        <div>
          <p className="caption-label text-[#A26F3E]">Administration</p>
          <h1 className="font-serif text-4xl text-[#1A1A1A] mt-2">CMS Éditorial</h1>
          <p className="text-sm text-[#6E4B2A] mt-2 max-w-xl leading-relaxed">
            Modifie les textes, images et couleurs de la page d'accueil publique sans toucher au code. Les modifications sont visibles immédiatement après sauvegarde.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <a
            href="/"
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 h-10 px-4 rounded-full border border-[#D4C3A5] text-[#1A1A1A] text-xs font-semibold tracking-wide hover:border-[#1A1A1A]"
            data-testid="admin-cms-preview"
          >
            <ExternalLink className="w-3.5 h-3.5" />
            Aperçu
          </a>
          <Button
            onClick={resetAll}
            variant="outline"
            className="rounded-full border-[#D4C3A5] h-10 px-4 text-xs"
            data-testid="admin-cms-reset"
          >
            <RotateCcw className="w-3.5 h-3.5 mr-1.5" />
            Réinitialiser
          </Button>
          <Button
            onClick={save}
            disabled={saving}
            className="rounded-full bg-[#C68E59] hover:bg-[#A26F3E] text-white h-10 px-5 text-xs font-semibold tracking-wide"
            data-testid="admin-cms-save"
          >
            {saving ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1.5" />}
            Enregistrer
          </Button>
        </div>
      </div>

      <div className="space-y-10">
        {/* ============ HERO ============ */}
        <Section title="Hero (section principale)" icon={ImageIcon}>
          <Field label="Sur-titre">
            <Input value={data.hero_eyebrow || ""} onChange={(e) => update("hero_eyebrow", e.target.value)} data-testid="cms-hero-eyebrow" />
          </Field>
          <div className="grid sm:grid-cols-3 gap-4">
            <Field label="Titre (partie 1)" hint="Avant l'italique">
              <Input value={data.hero_title_part1 || ""} onChange={(e) => update("hero_title_part1", e.target.value)} data-testid="cms-hero-title-1" />
            </Field>
            <Field label="Titre (italique)" hint="Mis en valeur en bronze">
              <Input value={data.hero_title_italic || ""} onChange={(e) => update("hero_title_italic", e.target.value)} data-testid="cms-hero-title-italic" />
            </Field>
            <Field label="Titre (partie 3)" hint="Après l'italique">
              <Input value={data.hero_title_part3 || ""} onChange={(e) => update("hero_title_part3", e.target.value)} data-testid="cms-hero-title-3" />
            </Field>
          </div>
          <Field label="Sous-titre">
            <Textarea rows={2} value={data.hero_subtitle || ""} onChange={(e) => update("hero_subtitle", e.target.value)} data-testid="cms-hero-subtitle" />
          </Field>
          <Field label="Liste des catégories" hint="Séparées par ·">
            <Textarea rows={2} value={data.hero_categories || ""} onChange={(e) => update("hero_categories", e.target.value)} data-testid="cms-hero-categories" />
          </Field>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="CTA principal">
              <Input value={data.hero_cta_primary || ""} onChange={(e) => update("hero_cta_primary", e.target.value)} data-testid="cms-hero-cta1" />
            </Field>
            <Field label="CTA secondaire">
              <Input value={data.hero_cta_secondary || ""} onChange={(e) => update("hero_cta_secondary", e.target.value)} data-testid="cms-hero-cta2" />
            </Field>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <ImageField
              label="Image gauche"
              value={data.hero_image_1}
              onChange={(url) => update("hero_image_1", url)}
              testid="cms-hero-img1"
            />
            <ImageField
              label="Image droite"
              value={data.hero_image_2}
              onChange={(url) => update("hero_image_2", url)}
              testid="cms-hero-img2"
            />
          </div>
          <div className="grid sm:grid-cols-2 gap-4 pt-2">
            <div className="rounded-2xl border border-[#EADDC9] bg-[#FAF3EA]/40 p-4">
              <p className="caption-label text-[#A26F3E] mb-3">Badge "Concours en cours"</p>
              <Field label="Sur-titre"><Input value={data.hero_badge_contest_label || ""} onChange={(e) => update("hero_badge_contest_label", e.target.value)} data-testid="cms-badge-contest-label" /></Field>
              <Field label="Titre"><Input value={data.hero_badge_contest_title || ""} onChange={(e) => update("hero_badge_contest_title", e.target.value)} data-testid="cms-badge-contest-title" /></Field>
              <Field label="Sous-titre"><Input value={data.hero_badge_contest_subtitle || ""} onChange={(e) => update("hero_badge_contest_subtitle", e.target.value)} data-testid="cms-badge-contest-subtitle" /></Field>
            </div>
            <div className="rounded-2xl border border-[#EADDC9] bg-[#FAF3EA]/40 p-4">
              <p className="caption-label text-[#A26F3E] mb-3">Badge "Communauté"</p>
              <Field label="Sur-titre"><Input value={data.hero_badge_creators_label || ""} onChange={(e) => update("hero_badge_creators_label", e.target.value)} data-testid="cms-badge-creators-label" /></Field>
              <Field label="Titre"><Input value={data.hero_badge_creators_title || ""} onChange={(e) => update("hero_badge_creators_title", e.target.value)} data-testid="cms-badge-creators-title" /></Field>
            </div>
          </div>
        </Section>

        {/* ============ ABOUT ============ */}
        <Section title="À propos d'Helencia" icon={ImageIcon}>
          <Field label="Sur-titre">
            <Input value={data.about_eyebrow || ""} onChange={(e) => update("about_eyebrow", e.target.value)} data-testid="cms-about-eyebrow" />
          </Field>
          <div className="grid sm:grid-cols-3 gap-4">
            <Field label="Titre (partie 1)"><Input value={data.about_title_part1 || ""} onChange={(e) => update("about_title_part1", e.target.value)} data-testid="cms-about-title-1" /></Field>
            <Field label="Titre (italique)"><Input value={data.about_title_italic || ""} onChange={(e) => update("about_title_italic", e.target.value)} data-testid="cms-about-title-italic" /></Field>
            <Field label="Titre (partie 3)"><Input value={data.about_title_part3 || ""} onChange={(e) => update("about_title_part3", e.target.value)} data-testid="cms-about-title-3" /></Field>
          </div>
          <Field label="Paragraphe 1"><Textarea rows={2} value={data.about_subtitle_1 || ""} onChange={(e) => update("about_subtitle_1", e.target.value)} data-testid="cms-about-sub1" /></Field>
          <Field label="Paragraphe 2"><Textarea rows={2} value={data.about_subtitle_2 || ""} onChange={(e) => update("about_subtitle_2", e.target.value)} data-testid="cms-about-sub2" /></Field>
          <ImageField label="Image" value={data.about_image} onChange={(url) => update("about_image", url)} testid="cms-about-img" />

          <div>
            <p className="caption-label text-[#A26F3E] mb-2">Statistiques (4 valeurs)</p>
            <div className="grid sm:grid-cols-2 gap-3">
              {(data.about_stats || []).slice(0, 4).map((s, i) => (
                <div key={i} className="rounded-xl border border-[#EADDC9] p-3 bg-white">
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-[10px] uppercase tracking-wider text-[#A26F3E]">Valeur</Label>
                      <Input value={s.v} onChange={(e) => updateStat(i, "v", e.target.value)} data-testid={`cms-stat-${i}-v`} />
                    </div>
                    <div>
                      <Label className="text-[10px] uppercase tracking-wider text-[#A26F3E]">Libellé</Label>
                      <Input value={s.l} onChange={(e) => updateStat(i, "l", e.target.value)} data-testid={`cms-stat-${i}-l`} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Section>

        {/* ============ COLORS ============ */}
        <Section title="Couleurs de marque" icon={Palette}>
          <div className="grid sm:grid-cols-2 gap-4">
            <ColorField label="Couleur principale (caramel)" value={data.accent_color || "#C68E59"} onChange={(v) => update("accent_color", v)} testid="cms-color-accent" />
            <ColorField label="Couleur secondaire (caramel foncé)" value={data.accent_color_dark || "#A26F3E"} onChange={(v) => update("accent_color_dark", v)} testid="cms-color-accent-dark" />
          </div>
          <p className="text-xs text-[#8B8680] leading-relaxed">
            Ces couleurs s'appliquent aux liens, sur-titres et accents sur la page d'accueil. La charte globale Helencia (typo, structure) reste intacte.
          </p>
        </Section>

        {/* Sticky bottom save bar on mobile */}
        <div className="sticky bottom-4 lg:bottom-6 z-20 flex justify-end">
          <Button
            onClick={save}
            disabled={saving}
            className="rounded-full bg-[#1A1A1A] hover:bg-[#2A1E12] text-white h-12 px-6 text-sm font-semibold tracking-wide shadow-[0_10px_30px_-10px_rgba(110,75,42,0.55)]"
            data-testid="admin-cms-save-bottom"
          >
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Enregistrer les modifications
          </Button>
        </div>
      </div>
    </div>
  );
}

/* ============================== Sub-components ============================== */
function Section({ title, icon: Icon, children }) {
  return (
    <section className="rounded-3xl bg-white border border-[#EADDC9] shadow-[0_4px_24px_rgba(110,75,42,0.05)] p-6 sm:p-8 space-y-5">
      <div className="flex items-center gap-3 pb-2 border-b border-[#EADDC9]">
        {Icon && <Icon className="w-4 h-4 text-[#A26F3E]" />}
        <h2 className="font-serif text-2xl text-[#1A1A1A]">{title}</h2>
      </div>
      {children}
    </section>
  );
}

function Field({ label, hint, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-[11px] uppercase tracking-[0.18em] text-[#A26F3E]">{label}</Label>
      {children}
      {hint && <p className="text-[11px] text-[#8B8680]">{hint}</p>}
    </div>
  );
}

function ImageField({ label, value, onChange, testid }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/cms/home/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(data.url);
      toast.success("Image téléchargée");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Échec de l'envoi");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div className="space-y-2">
      <Label className="text-[11px] uppercase tracking-[0.18em] text-[#A26F3E]">{label}</Label>
      <div className="flex gap-3">
        <div className="w-28 h-36 rounded-2xl overflow-hidden bg-[#FAF3EA] border border-[#EADDC9] flex-shrink-0">
          {value ? (
            <img src={value} alt="" className="w-full h-full object-cover" data-testid={`${testid}-preview`} />
          ) : (
            <div className="w-full h-full flex items-center justify-center"><ImageIcon className="w-5 h-5 text-[#C68E59]" /></div>
          )}
        </div>
        <div className="flex-1 space-y-2">
          <Input
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            placeholder="https://… ou /api/files/cms_home/…"
            data-testid={`${testid}-url`}
          />
          <Button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
            variant="outline"
            className="rounded-full border-[#D4C3A5] h-9 px-4 text-xs"
            data-testid={`${testid}-upload`}
          >
            {uploading ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Upload className="w-3.5 h-3.5 mr-1.5" />}
            Téléverser une image
          </Button>
          <input
            ref={inputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
            className="hidden"
            onChange={handleUpload}
          />
        </div>
      </div>
    </div>
  );
}

function ColorField({ label, value, onChange, testid }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-[11px] uppercase tracking-[0.18em] text-[#A26F3E]">{label}</Label>
      <div className="flex items-center gap-3">
        <input
          type="color"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-14 h-12 rounded-xl border border-[#EADDC9] bg-white cursor-pointer"
          data-testid={`${testid}-picker`}
        />
        <Input value={value} onChange={(e) => onChange(e.target.value)} className="font-mono" data-testid={`${testid}-text`} />
      </div>
    </div>
  );
}
